<script setup>
const props = defineProps({
  inputPrefix: {
    type: String,
    required: true,
  },
  filter: {
    type: Object,
    required: true,
  },
  boundaries: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  rangeGap: {
    type: Number,
    required: false,
    default: 1,
  },
  rangeStep: {
    type: Number,
    required: false,
    default: 1,
  },
  minRangeLabel: {
    type: String,
    required: false,
    default: 'Minimum',
  },
  maxRangeLabel: {
    type: String,
    required: false,
    default: 'Maximum',
  },
  showTextFields: {
    type: Boolean,
    required: false,
    default: true,
  },
  showValueLabels: {
    type: Boolean,
    required: false,
    default: false,
  },
  // Array of objects in the form {value: 1, label: 'low'}
  // which maps the value of the slider to a label display value
  valueLabelMapping: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  omitMinBoundaryFromFilter: {
    type: Boolean,
    required: false,
    default: false,
  },
  omitMaxBoundaryFromFilter: {
    type: Boolean,
    required: false,
    default: false,
  },
})
const emit = defineEmits(['change'])
const min = ref(Number(props.boundaries[0]))
const max = ref(Number(props.boundaries[1]))
const rangeLow = ref(Number(props.boundaries[0]))
const rangeHigh = ref(Number(props.boundaries[1]))
const initialLow = ref(Number(props.boundaries[0]))
const initialHigh = ref(Number(props.boundaries[1]))

const textRangeMin = computed(() => {
  return props.omitMinBoundaryFromFilter &&
    Number(rangeLow.value) === Number(props.boundaries[0])
    ? undefined
    : rangeLow.value
})
const textRangeMax = computed(() => {
  return props.omitMaxBoundaryFromFilter &&
    Number(rangeHigh.value) === Number(props.boundaries[1])
    ? undefined
    : rangeHigh.value
})
const rangeValueText = computed(() => {
  let valueLabelLow = Number(rangeLow.value || Number(props.boundaries[0]))
  const valueLabelLowMapping = props.valueLabelMapping.find(
    (x) => x.value === valueLabelLow,
  )
  if (valueLabelLowMapping) {
    valueLabelLow = valueLabelLowMapping.label
  }

  let valueLabelHigh =
    rangeHigh.value === undefined ||
    rangeHigh.value === null ||
    rangeHigh.value === ''
      ? Number(props.boundaries[1])
      : Number(rangeHigh.value)
  const valueLabelHighMapping = props.valueLabelMapping.find(
    (x) => x.value === valueLabelHigh,
  )
  if (valueLabelHighMapping) {
    valueLabelHigh = valueLabelHighMapping.label
  }
  if (valueLabelLow === valueLabelHigh) {
    return valueLabelLow
  }

  return `${valueLabelLow} - ${valueLabelHigh}`
})
// Compare distance from the side and the most distance gets to be on top
const zIndexLow = computed(() => {
  if (
    Number(rangeLow.value) - Number(props.boundaries[0]) >
    Number(props.boundaries[1]) - Number(rangeHigh.value)
  ) {
    return 10
  }
  return 2
})
const zIndexHigh = computed(() => {
  if (
    Number(rangeLow.value) - Number(props.boundaries[0]) <
    Number(props.boundaries[1]) - Number(rangeHigh.value)
  ) {
    return 10
  }
  return 2
})

function rangeChanged(event) {
  rangeLow.value = Number(rangeLow.value || min.value)
  rangeHigh.value =
    rangeHigh.value === undefined ||
    rangeHigh.value === null ||
    rangeHigh.value === ''
      ? Number(max.value)
      : Number(rangeHigh.value)

  // This ensures the range sliders don't cross each other
  if (rangeHigh.value - rangeLow.value < props.rangeGap) {
    if (
      event.target.id === `${props.inputPrefix}-minimum-range` ||
      event.target.id === `${props.inputPrefix}-minimum-input`
    ) {
      rangeLow.value = rangeHigh.value - props.rangeGap
    } else if (
      event.target.id === `${props.inputPrefix}-maximum-range` ||
      event.target.id === `${props.inputPrefix}-maximum-input`
    ) {
      rangeHigh.value = rangeLow.value + props.rangeGap
    }
  }
}
function textInputCommit(event) {
  if (event.target.id === `${props.inputPrefix}-minimum-input`) {
    rangeLow.value = event.target.value
  }
  if (event.target.id === `${props.inputPrefix}-maximum-input`) {
    rangeHigh.value = event.target.value
  }
  rangeChanged(event)
  commitChange()
}
function commitChange() {
  if (
    Number(initialLow.value) === Number(rangeLow.value) &&
    Number(initialHigh.value) === Number(rangeHigh.value)
  )
    return
  emit('change', textRangeMin.value, textRangeMax.value)
}
function setInputFocus(event) {
  nextTick(() => {
    event.target.focus()
  })
}
function resetSliderValues(filter, boundaries) {
  min.value = boundaries[0]
  max.value = boundaries[1]
  rangeLow.value = Number(filter?.from ?? min.value)
  rangeHigh.value =
    filter?.to === undefined || filter?.to === null
      ? Number(max.value)
      : Number(filter?.to)
  initialLow.value = Number(filter?.from ?? min.value)
  initialHigh.value =
    filter?.to === undefined || filter?.to === null
      ? Number(max.value)
      : Number(filter?.to)
}

onMounted(() => {
  resetSliderValues(props.filter, props.boundaries)
})

watch(
  () => props.filter,
  (newFilter) => {
    resetSliderValues(newFilter, props.boundaries)
  },
)
</script>

<template>
  <div>
    <div class="rangeslidermin-max">
      <label :for="`${inputPrefix}-minimum-range`" class="sr-only">{{
        minRangeLabel
      }}</label>
      <input
        :id="`${inputPrefix}-minimum-range`"
        v-model="rangeLow"
        type="range"
        :name="`${inputPrefix}-minimum-range`"
        :min="min"
        :max="max"
        :style="{ 'z-index': zIndexLow }"
        :step="rangeStep"
        data-popup-enabled="true"
        data-show-value="true"
        @blur="commitChange"
        @touchend="commitChange"
        @mouseup="commitChange"
        @input="rangeChanged"
      />
      <label :for="`${inputPrefix}-maximum-range`" class="sr-only">{{
        maxRangeLabel
      }}</label>
      <input
        :id="`${inputPrefix}-maximum-range`"
        v-model="rangeHigh"
        type="range"
        :name="`${inputPrefix}-maximum-range`"
        :min="min"
        :max="max"
        :style="{ 'z-index': zIndexHigh }"
        :step="rangeStep"
        data-popup-enabled="true"
        data-show-value="true"
        @blur="commitChange"
        @touchend="commitChange"
        @mouseup="commitChange"
        @input="rangeChanged"
      />
    </div>
    <div v-if="showTextFields" class="row range-inputs">
      <div class="columns small-6">
        <label :for="`${inputPrefix}-minimum-input`">
          <slot name="min-label">Min</slot>
        </label>
        <input
          :id="`${inputPrefix}-minimum-input`"
          :value="textRangeMin"
          type="number"
          :step="rangeStep"
          @keyup.enter="textInputCommit"
          @blur="textInputCommit"
          @click="setInputFocus"
        />
      </div>
      <div class="columns small-6">
        <label :for="`${inputPrefix}-maximum-input`">
          <slot name="max-label">Max</slot>
        </label>
        <input
          :id="`${inputPrefix}-maximum-input`"
          :value="textRangeMax"
          type="number"
          :step="rangeStep"
          @keyup.enter="textInputCommit"
          @blur="textInputCommit"
          @click="setInputFocus"
        />
      </div>
    </div>
    <div v-if="showValueLabels" class="row range-values">
      {{ rangeValueText }}
    </div>
  </div>
</template>
